import React, { useEffect, useMemo, useState } from 'react';
import DashboardShell from './components/DashboardShell';
import TodoTeamSettings from './components/TodoTeamSettings';
import BackgroundTaskMonitor from './components/BackgroundTaskMonitor';
import CMECFFilingSettings from './components/CMECFFilingSettings';
import TitleSearchSettings from './components/TitleSearchSettings';
import WageOrderReport from './components/WageOrderReport';
import TrusteeFeeAudit from './components/TrusteeFeeAudit';
import CommonCreditorCleanupDashboard from './components/CommonCreditorCleanupDashboard';
import { loadMeansTestStandards } from '../utils/meansTestStandards';

// FirmManagementPage
// - Loads firm settings + attorneys from Django
// - Allows updating firm settings
// - Allows add/edit/delete attorneys and Today's ToDos employees

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

function buildUrl(path) {
  if (!path.startsWith('/')) path = `/${path}`;
  return `${API_BASE}${path}`;
}

function getAuthHeader() {
  // Try common token keys used across projects
  const token =
    localStorage.getItem('accessToken')

  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function apiFetch(path, options = {}) {
  const headers = {
    'Content-Type': 'application/json',
    ...getAuthHeader(),
    ...(options.headers || {}),
  };

  const res = await fetch(buildUrl(path), {
    ...options,
    headers,
  });

  const contentType = res.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');
  const data = isJson ? await res.json().catch(() => null) : await res.text().catch(() => null);

  if (!res.ok) {
    const message =
      (data && typeof data === 'object' && (data.detail || data.error || data.message)) ||
      (typeof data === 'string' && data) ||
      `Request failed (${res.status})`;
    const err = new Error(message);
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data;
}

function Modal({ open, title, children, onClose }) {
  if (!open) return null;
  return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
                  <div className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-lg bg-white shadow-lg flex flex-col">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="rounded-md px-3 py-1 text-sm text-gray-700 hover:bg-gray-100"
          >
            Close
          </button>
        </div>
                        <div className="p-4 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="bg-white rounded shadow p-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">{label}</div>
      <div className="mt-2">{children}</div>
    </div>
  );
}
function Labeled({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold text-gray-700">{label}</div>
      <div className="mt-1">{children}</div>
    </label>
  );
}
function Input({ value, onChange, placeholder, type = 'text' }) {
  return (
    <input
      type={type}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
    />
  );
}

function formatStandardsDate(value) {
  if (!value) return 'Not updated through the account page yet';
  const [year, month, day] = value.split('-');
  return `${month}/${day}/${year}`;
}

function EmailListEditor({ value, onChange, placeholder }) {
  const [draft, setDraft] = useState('');
  const [inputError, setInputError] = useState('');
  const recipients = Array.isArray(value) ? value : [];

  function addRecipients() {
    const candidates = draft
      .split(/[\s,;]+/)
      .map((email) => email.trim().toLowerCase())
      .filter(Boolean);
    if (!candidates.length) return;

    const invalid = candidates.find(
      (email) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    );
    if (invalid) {
      setInputError(`${invalid} is not a valid email address.`);
      return;
    }

    onChange([...new Set([...recipients, ...candidates])]);
    setDraft('');
    setInputError('');
  }

  function removeRecipient(emailToRemove) {
    onChange(recipients.filter((email) => email !== emailToRemove));
  }

  return (
    <div>
      <div className="flex flex-wrap gap-2 min-h-7">
        {recipients.length ? recipients.map((email) => (
          <span
            key={email}
            className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-800"
          >
            {email}
            <button
              type="button"
              onClick={() => removeRecipient(email)}
              className="text-indigo-500 hover:text-red-600"
              aria-label={`Remove ${email}`}
            >
              ×
            </button>
          </span>
        )) : (
          <span className="text-xs text-gray-500">No internal recipients</span>
        )}
      </div>
      <div className="mt-3 flex gap-2">
        <input
          type="text"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              addRecipients();
            }
          }}
          placeholder={placeholder}
          className="min-w-0 flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <button
          type="button"
          onClick={addRecipients}
          className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
        >
          Add
        </button>
      </div>
      {inputError && <p className="mt-1 text-xs text-red-600">{inputError}</p>}
    </div>
  );
}

function Select({ value, onChange, options }) {
  return (
    <select
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
    >
      <option value="">Select...</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function Button({ children, onClick, variant = 'primary', disabled = false, title }) {
  const cls =
    variant === 'danger'
      ? 'bg-red-600 hover:bg-red-700'
      : variant === 'secondary'
        ? 'bg-gray-100 hover:bg-gray-200 text-gray-900'
        : 'bg-indigo-600 hover:bg-indigo-700';

  const textCls = variant === 'secondary' ? 'text-gray-900' : 'text-white';

  return (
    <button
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-semibold ${textCls} ${cls} disabled:opacity-60 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
}

const DEFAULT_FIRM = {
  name: '',
  street: '',
  street2: '',
  city: '',
  state: '',
  zip_code: '',
  filing_district: '',
  division: '',
  phone_number: '',
  email: '',
  fax_number: '',
  base_chapter_7: 1200,
  chapter_7_joint_additional: 100,
  chapter_7_reaffirmation_additional: 50,
  chapter_7_self_employed_additional: 100,
  chapter_13_unsecured_fee: 4000,
  chapter_13_max_fee: 4500,
  quick_intake_notification_recipients: [],
  getting_started_notification_recipients: [],
};

const DEFAULT_ATTORNEY = {
  id: null,
  first_name: '',
  second_name: '',
  last_name: '',
  bar_number: '',
  email: '',
  phone_number: '',
  fax_number: '',
  street: '',
  street2: '',
  city: '',
  state: '',
  zip_code: '',
  filing_district: '',
  division: '',
  base_chapter_7: 1200,
  chapter_7_joint_additional: 100,
  chapter_7_reaffirmation_additional: 50,
  chapter_7_self_employed_additional: 100,
  chapter_13_unsecured_fee: 4000,
  chapter_13_max_fee: 4500,
};

function FirmManagementPage() {
  const [loading, setLoading] = useState(true);
  const [savingFirm, setSavingFirm] = useState(false);
  const [firm, setFirm] = useState(DEFAULT_FIRM);
  const [attorneys, setAttorneys] = useState([]);

  const [toast, setToast] = useState(null);
  const [error, setError] = useState(null);

  const [attorneyModalOpen, setAttorneyModalOpen] = useState(false);
  const [attorneyWorking, setAttorneyWorking] = useState(false);
  const [attorneyForm, setAttorneyForm] = useState(DEFAULT_ATTORNEY);
  const isEditingAttorney = useMemo(() => !!attorneyForm?.id, [attorneyForm]);
  const [standardsOverview, setStandardsOverview] = useState({ active: null, recent: [] });
  const [standardsEffectiveDate, setStandardsEffectiveDate] = useState('');
  const [standardsJob, setStandardsJob] = useState(null);
  const [standardsLoading, setStandardsLoading] = useState(true);
  const [standardsError, setStandardsError] = useState('');
  const standardsJobId = standardsJob?.id;
  const standardsJobStatus = standardsJob?.status;

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await apiFetch('/firm/account/', { method: 'GET' });
        if (!mounted) return;
        setFirm({ ...DEFAULT_FIRM, ...(data?.firm || {}) });
        setAttorneys(Array.isArray(data?.attorneys) ? data.attorneys : []);
      } catch (e) {
        if (!mounted) return;
        setError(e.message || 'Failed to load firm settings');
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const overview = await apiFetch('/firm/means-test-standards/updates/', { method: 'GET' });
        if (!mounted) return;
        setStandardsOverview({
          active: overview?.active || null,
          recent: Array.isArray(overview?.recent) ? overview.recent : [],
        });
        if (overview?.active?.effective_date) {
          setStandardsEffectiveDate(overview.active.effective_date);
        }
        const recent = Array.isArray(overview?.recent) ? overview.recent : [];
        const unfinished = recent.find(
          (item) => item.status === 'queued' || item.status === 'running'
        );
        setStandardsJob(unfinished || recent[0] || null);
      } catch (requestError) {
        if (mounted) {
          setStandardsError(requestError.message || 'Could not load means-test update status.');
        }
      } finally {
        if (mounted) setStandardsLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!standardsJobId || !['queued', 'running'].includes(standardsJobStatus)) return undefined;

    let cancelled = false;
    let polling = false;
    const poll = async () => {
      if (polling || cancelled) return;
      polling = true;
      try {
        const latest = await apiFetch(
          `/firm/means-test-standards/updates/${standardsJobId}/`,
          { method: 'GET' }
        );
        if (cancelled) return;
        setStandardsJob(latest);

        if (latest.status === 'completed' || latest.status === 'failed') {
          const overview = await apiFetch('/firm/means-test-standards/updates/', { method: 'GET' });
          if (cancelled) return;
          setStandardsOverview({
            active: overview?.active || null,
            recent: Array.isArray(overview?.recent) ? overview.recent : [],
          });
          if (latest.status === 'completed') {
            setStandardsError('');
            setStandardsEffectiveDate(latest.effective_date);
            setToast('New means-test standards are active');
            setTimeout(() => setToast(null), 3500);
            loadMeansTestStandards({ force: true }).catch((requestError) => {
              console.error('Could not refresh standards in this browser:', requestError);
            });
          }
        }
      } catch (requestError) {
        if (!cancelled) {
          setStandardsError(requestError.message || 'Could not check the update status.');
        }
      } finally {
        polling = false;
      }
    };

    poll();
    const timer = window.setInterval(poll, 2500);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [standardsJobId, standardsJobStatus]);

  async function startStandardsUpdate() {
    if (!standardsEffectiveDate) {
      setStandardsError('Choose the effective date published by the U.S. Trustee Program.');
      return;
    }
    setStandardsLoading(true);
    setStandardsError('');
    try {
      const job = await apiFetch('/firm/means-test-standards/updates/', {
        method: 'POST',
        body: JSON.stringify({ effective_date: standardsEffectiveDate }),
      });
      setStandardsJob(job);
    } catch (requestError) {
      setStandardsError(requestError.message || 'Could not start the standards update.');
    } finally {
      setStandardsLoading(false);
    }
  }

  function updateFirmField(key, val) {
    setFirm((prev) => ({ ...prev, [key]: val }));
  }

  async function saveFirmSettings() {
    setSavingFirm(true);
    setError(null);
    try {
      // Send only the fields we manage
      const payload = {
        name: firm.name,
        street: firm.street,
        street2: firm.street2 || null,
        city: firm.city,
        state: firm.state,
        zip_code: firm.zip_code,
        filing_district: firm.filing_district,
        division: firm.division,
        phone_number: firm.phone_number,
        email: firm.email,
        fax_number: firm.fax_number || null,
        base_chapter_7: firm.base_chapter_7,
        chapter_7_joint_additional: firm.chapter_7_joint_additional,
        chapter_7_reaffirmation_additional: firm.chapter_7_reaffirmation_additional,
        chapter_7_self_employed_additional: firm.chapter_7_self_employed_additional,
        chapter_13_unsecured_fee: firm.chapter_13_unsecured_fee,
        chapter_13_max_fee: firm.chapter_13_max_fee,
        quick_intake_notification_recipients: firm.quick_intake_notification_recipients,
        getting_started_notification_recipients: firm.getting_started_notification_recipients,
      };

      const data = await apiFetch('/firm/account/', {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });

      setFirm({ ...DEFAULT_FIRM, ...(data?.firm || {}) });
      setToast('Firm settings saved');
      setTimeout(() => setToast(null), 2500);
    } catch (e) {
      setError(e.message || 'Failed to save firm settings');
    } finally {
      setSavingFirm(false);
    }
  }

  function openAddAttorney() {
    setAttorneyForm({ ...DEFAULT_ATTORNEY });
    setAttorneyModalOpen(true);
  }

  function openEditAttorney(a) {
    setAttorneyForm({
      ...DEFAULT_ATTORNEY,
      id: a.id,
      first_name: a.first_name || '',
      second_name: a.second_name || '',
      last_name: a.last_name || '',
      bar_number: a.bar_number || '',
      email: a.email || '',
      phone_number: a.phone_number || '',
      fax_number: a.fax_number || '',
      street: a.street || '',
      street2: a.street2 || '',
      city: a.city || '',
      state: a.state || '',
      zip_code: a.zip_code || '',
      filing_district: a.filing_district || '',
      division: a.division || '',
      base_chapter_7: a.base_chapter_7 ?? DEFAULT_ATTORNEY.base_chapter_7,
      chapter_7_joint_additional: a.chapter_7_joint_additional ?? DEFAULT_ATTORNEY.chapter_7_joint_additional,
      chapter_7_reaffirmation_additional: a.chapter_7_reaffirmation_additional ?? DEFAULT_ATTORNEY.chapter_7_reaffirmation_additional,
      chapter_7_self_employed_additional: a.chapter_7_self_employed_additional ?? DEFAULT_ATTORNEY.chapter_7_self_employed_additional,
      chapter_13_unsecured_fee: a.chapter_13_unsecured_fee ?? DEFAULT_ATTORNEY.chapter_13_unsecured_fee,
      chapter_13_max_fee: a.chapter_13_max_fee ?? DEFAULT_ATTORNEY.chapter_13_max_fee,
    });
    setAttorneyModalOpen(true);
  }

  function closeAttorneyModal() {
    if (attorneyWorking) return;
    setAttorneyModalOpen(false);
    setAttorneyForm({ ...DEFAULT_ATTORNEY });
  }

  function updateAttorneyField(key, val) {
    setAttorneyForm((prev) => ({ ...prev, [key]: val }));
  }

  async function saveAttorney() {
    setAttorneyWorking(true);
    setError(null);

    const payload = {
      first_name: attorneyForm.first_name,
      second_name: attorneyForm.second_name || null,
      last_name: attorneyForm.last_name,
      bar_number: attorneyForm.bar_number,
      email: attorneyForm.email,
      phone_number: attorneyForm.phone_number,
      fax_number: attorneyForm.fax_number || null,
      street: attorneyForm.street,
      street2: attorneyForm.street2 || null,
      city: attorneyForm.city,
      state: attorneyForm.state,
      zip_code: attorneyForm.zip_code,
      filing_district: attorneyForm.filing_district,
      division: attorneyForm.division,
      base_chapter_7: attorneyForm.base_chapter_7,
      chapter_7_joint_additional: attorneyForm.chapter_7_joint_additional,
      chapter_7_reaffirmation_additional: attorneyForm.chapter_7_reaffirmation_additional,
      chapter_7_self_employed_additional: attorneyForm.chapter_7_self_employed_additional,
      chapter_13_unsecured_fee: attorneyForm.chapter_13_unsecured_fee,
      chapter_13_max_fee: attorneyForm.chapter_13_max_fee,
    };

    try {
      if (isEditingAttorney) {
        const updated = await apiFetch(`/firm/attorneys/${attorneyForm.id}/`, {
          method: 'PATCH',
          body: JSON.stringify(payload),
        });
        setAttorneys((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
        setToast('Attorney updated');
      } else {
        const created = await apiFetch('/firm/attorneys/', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        setAttorneys((prev) => [created, ...prev]);
        setToast('Attorney added');
      }
      setTimeout(() => setToast(null), 2500);
      closeAttorneyModal();
    } catch (e) {
      setError(e.message || 'Failed to save attorney');
    } finally {
      setAttorneyWorking(false);
    }
  }

  async function deleteAttorney(a) {
    const ok = window.confirm(`Delete attorney ${a.first_name} ${a.last_name}?`);
    if (!ok) return;

    setError(null);
    try {
      await apiFetch(`/firm/attorneys/${a.id}/`, { method: 'DELETE' });
      setAttorneys((prev) => prev.filter((x) => x.id !== a.id));
      setToast('Attorney deleted');
      setTimeout(() => setToast(null), 2500);
    } catch (e) {
      setError(e.message || 'Failed to delete attorney');
    }
  }

  const districtOptions = useMemo(
    () => [
      { value: 'Eastern District of Tennessee', label: 'Eastern District of Tennessee' },
      { value: 'Middle District of Tennessee', label: 'Middle District of Tennessee' },
      { value: 'TN Western', label: 'TN Western' },
      { value: 'KY Eastern', label: 'KY Eastern' },
      { value: 'KY Western', label: 'KY Western' },
      { value: 'NC Western', label: 'NC Western' },
      { value: 'VA Western', label: 'VA Western' },
      { value: 'Other', label: 'Other' },
    ],
    []
  );

  if (loading) {
    return (
      <DashboardShell>
        <div className="bg-white rounded shadow p-6">
          <div className="text-lg font-semibold text-gray-900">Loading firm settings…</div>
          <div className="mt-2 text-sm text-gray-600">
            If this hangs, make sure your JWT is present in localStorage and your API is reachable.
          </div>
        </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell>
      <div className="space-y-8">
        <div className="bg-white rounded shadow p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Account Management</h1>
              <p className="mt-1 text-sm text-gray-600">Update firm settings and manage attorneys, employees, and todo assignments.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="secondary"
                onClick={() => document.getElementById('title-search-accounts')?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start',
                })}
              >
                Title Search Logins
              </Button>
              <Button onClick={saveFirmSettings} disabled={savingFirm}>
                {savingFirm ? 'Saving…' : 'Save Firm Settings'}
              </Button>
              <Button variant="secondary" onClick={openAddAttorney}>Add Attorney</Button>
            </div>
          </div>

          {toast && (
            <div className="mt-4 px-4 py-3 rounded-md bg-green-50 border border-green-400 text-green-700">
              {toast}
            </div>
          )}

          {error && (
            <div className="mt-4 px-4 py-3 rounded-md bg-red-50 border border-red-400 text-red-700">
              {error}
            </div>
          )}
        </div>

        <section className="bg-white rounded shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Firm Settings</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Field label="Firm Name">
              <Input value={firm.name} onChange={(v) => updateFirmField('name', v)} placeholder="Cisowski Law" />
            </Field>

            <Field label="Phone">
              <Input value={firm.phone_number} onChange={(v) => updateFirmField('phone_number', v)} placeholder="(555) 123-4567" />
            </Field>

            <Field label="Email">
              <Input value={firm.email} onChange={(v) => updateFirmField('email', v)} placeholder="firm@example.com" type="email" />
            </Field>

            <Field label="Street">
              <Input value={firm.street} onChange={(v) => updateFirmField('street', v)} placeholder="3119 Bristol Hwy" />
            </Field>

            <Field label="Street 2">
              <Input value={firm.street2} onChange={(v) => updateFirmField('street2', v)} placeholder="Ste 217" />
            </Field>

            <Field label="City / State / Zip">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Input value={firm.city} onChange={(v) => updateFirmField('city', v)} placeholder="City" />
                <Input value={firm.state} onChange={(v) => updateFirmField('state', v)} placeholder="State" />
                <Input value={firm.zip_code} onChange={(v) => updateFirmField('zip_code', v)} placeholder="Zip" />
              </div>
            </Field>

            <Field label="Filing District">
              <Select
                value={firm.filing_district}
                onChange={(v) => updateFirmField('filing_district', v)}
                options={districtOptions}
              />
            </Field>

            <Field label="Division">
              <Input value={firm.division} onChange={(v) => updateFirmField('division', v)} placeholder="Northeastern" />
            </Field>

            <Field label="Fax">
              <Input value={firm.fax_number} onChange={(v) => updateFirmField('fax_number', v)} placeholder="Optional" />
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            <Field label="Chapter 7 Base Fee">
              <Input
                type="number"
                value={firm.base_chapter_7}
                onChange={(v) => updateFirmField('base_chapter_7', v)}
                placeholder="1200"
              />
            </Field>
            <Field label="Joint Additional">
              <Input
                type="number"
                value={firm.chapter_7_joint_additional}
                onChange={(v) => updateFirmField('chapter_7_joint_additional', v)}
                placeholder="100"
              />
            </Field>
            <Field label="Reaffirmation Additional">
              <Input
                type="number"
                value={firm.chapter_7_reaffirmation_additional}
                onChange={(v) => updateFirmField('chapter_7_reaffirmation_additional', v)}
                placeholder="50"
              />
            </Field>
            <Field label="Self-Employed Additional">
              <Input
                type="number"
                value={firm.chapter_7_self_employed_additional}
                onChange={(v) => updateFirmField('chapter_7_self_employed_additional', v)}
                placeholder="100"
              />
            </Field>
            <Field label="Chapter 13 Unsecured Fee">
              <Input
                type="number"
                value={firm.chapter_13_unsecured_fee}
                onChange={(v) => updateFirmField('chapter_13_unsecured_fee', v)}
                placeholder="4000"
              />
            </Field>
            <Field label="Chapter 13 Max Fee">
              <Input
                type="number"
                value={firm.chapter_13_max_fee}
                onChange={(v) => updateFirmField('chapter_13_max_fee', v)}
                placeholder="4500"
              />
            </Field>
          </div>

          <h3 className="mt-7 text-base font-semibold text-gray-900">Email Notifications</h3>
          <p className="mt-1 text-sm text-gray-600">
            Add or remove the people who should receive internal copies of these emails.
          </p>
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Field label="Quick Intake Recipients">
              <EmailListEditor
                value={firm.quick_intake_notification_recipients}
                onChange={(value) => updateFirmField('quick_intake_notification_recipients', value)}
                placeholder="name@firm.com"
              />
              <p className="mt-2 text-xs text-gray-500">
                These people receive the internal notification when a quick intake is submitted.
              </p>
            </Field>
            <Field label="Getting Started Recipients">
              <EmailListEditor
                value={firm.getting_started_notification_recipients}
                onChange={(value) => updateFirmField('getting_started_notification_recipients', value)}
                placeholder="name@firm.com"
              />
              <p className="mt-2 text-xs text-gray-500">
                These people are copied when the Getting Started email is sent to a client.
              </p>
            </Field>
          </div>
        </section>

        <TitleSearchSettings />

        <CommonCreditorCleanupDashboard />

        <BackgroundTaskMonitor />

        <WageOrderReport />

        <TrusteeFeeAudit />

        <CMECFFilingSettings />

        <section className="bg-white rounded shadow p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-2xl">
              <h2 className="text-xl font-semibold text-gray-900">Means Test Standards</h2>
              <p className="mt-1 text-sm text-gray-600">
                Enter the effective date shown on the U.S. Trustee Program data pages. The system
                will download and validate the median income, national expenses, health care,
                housing, transportation, and Chapter 13 administrative-expense data.
              </p>
            </div>
            <div className="rounded-lg border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm">
              <div className="text-xs font-semibold uppercase tracking-wide text-indigo-700">
                Active effective date
              </div>
              <div className="mt-1 font-semibold text-indigo-950">
                {formatStandardsDate(standardsOverview.active?.effective_date)}
              </div>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,320px)_auto] lg:items-end">
            <label className="block">
              <span className="text-sm font-semibold text-gray-800">New standards effective date</span>
              <input
                type="date"
                value={standardsEffectiveDate}
                onChange={(event) => setStandardsEffectiveDate(event.target.value)}
                disabled={standardsJob && ['queued', 'running'].includes(standardsJob.status)}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
              />
            </label>
            <div>
              <Button
                onClick={startStandardsUpdate}
                disabled={
                  standardsLoading
                  || !standardsEffectiveDate
                  || (standardsJob && ['queued', 'running'].includes(standardsJob.status))
                }
              >
                {standardsJob && ['queued', 'running'].includes(standardsJob.status)
                  ? 'Updating…'
                  : 'Download and Activate Standards'}
              </Button>
            </div>
          </div>

          <p className="mt-3 text-xs text-gray-500">
            The current standards stay active unless every required table downloads and passes validation.
          </p>

          {standardsJob && (
            <div className={`mt-5 rounded-lg border p-4 ${
              standardsJob.status === 'failed'
                ? 'border-red-200 bg-red-50'
                : standardsJob.status === 'completed'
                  ? 'border-green-200 bg-green-50'
                  : 'border-blue-200 bg-blue-50'
            }`}>
              <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <span className="font-semibold text-gray-900">
                  {formatStandardsDate(standardsJob.effective_date)} update
                </span>
                <span className="font-medium capitalize text-gray-700">{standardsJob.status}</span>
              </div>
              <p className="mt-1 text-sm text-gray-700">{standardsJob.status_message}</p>
              {['queued', 'running'].includes(standardsJob.status) && (
                <div className="mt-3 h-2 overflow-hidden rounded-full bg-blue-100">
                  <div
                    className="h-full rounded-full bg-indigo-600 transition-all duration-500"
                    style={{ width: `${Math.max(2, standardsJob.progress || 0)}%` }}
                  />
                </div>
              )}
              {standardsJob.error_message && (
                <p className="mt-3 whitespace-pre-wrap text-sm text-red-700">
                  {standardsJob.error_message}
                </p>
              )}
            </div>
          )}

          {standardsError && (
            <div className="mt-4 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
              {standardsError}
            </div>
          )}

          {standardsOverview.recent.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-gray-900">Recent updates</h3>
              <div className="mt-2 divide-y divide-gray-200 rounded-lg border border-gray-200">
                {standardsOverview.recent.map((item) => (
                  <div key={item.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm">
                    <span className="font-medium text-gray-800">
                      Effective {formatStandardsDate(item.effective_date)}
                    </span>
                    <span className={`capitalize ${
                      item.status === 'completed'
                        ? 'text-green-700'
                        : item.status === 'failed'
                          ? 'text-red-700'
                          : 'text-blue-700'
                    }`}>
                      {item.status}{item.is_active ? ' · active' : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        <section className="bg-white rounded shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Attorneys</h2>
            <Button variant="secondary" onClick={openAddAttorney}>Add Attorney</Button>
          </div>

          <div className="bg-white rounded shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left text-gray-700">
                    <th className="px-4 py-3 font-semibold">Name</th>
                    <th className="px-4 py-3 font-semibold">Email</th>
                    <th className="px-4 py-3 font-semibold">Phone</th>
                    <th className="px-4 py-3 font-semibold">Bar #</th>
                    <th className="px-4 py-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {attorneys.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-5 text-gray-600">
                        No attorneys yet. Click “Add Attorney”.
                      </td>
                    </tr>
                  ) : (
                    attorneys.map((a) => (
                      <tr key={a.id} className="border-t border-gray-200">
                        <td className="px-4 py-3">
                          <div className="font-semibold text-gray-900">
                            {a.first_name} {a.second_name ? `${a.second_name} ` : ''}{a.last_name}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-700">{a.email || '—'}</td>
                        <td className="px-4 py-3 text-gray-700">{a.phone_number || '—'}</td>
                        <td className="px-4 py-3 text-gray-700">{a.bar_number}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-2">
                            <Button variant="secondary" onClick={() => openEditAttorney(a)}>Edit</Button>
                            <Button variant="danger" onClick={() => deleteAttorney(a)}>Delete</Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <TodoTeamSettings />

        <Modal
          open={attorneyModalOpen}
          title={isEditingAttorney ? 'Edit Attorney' : 'Add Attorney'}
          onClose={closeAttorneyModal}
        >
                    <div className="space-y-4">
            {/* Basics */}
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="mb-3 flex items-center justify-between">
                <div className="text-sm font-semibold text-gray-900">Basics</div>
                <div className="text-xs text-gray-500">Name, bar number</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Labeled label="First Name">
                  <Input value={attorneyForm.first_name} onChange={(v) => updateAttorneyField('first_name', v)} placeholder="First" />
                </Labeled>
                <Labeled label="Middle / Second Name">
                  <Input value={attorneyForm.second_name} onChange={(v) => updateAttorneyField('second_name', v)} placeholder="Optional" />
                </Labeled>
                <Labeled label="Last Name">
                  <Input value={attorneyForm.last_name} onChange={(v) => updateAttorneyField('last_name', v)} placeholder="Last" />
                </Labeled>
                <Labeled label="Bar Number">
                  <Input value={attorneyForm.bar_number} onChange={(v) => updateAttorneyField('bar_number', v)} placeholder="BPR####" />
                </Labeled>
              </div>
            </div>

            {/* Contact */}
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="mb-3 flex items-center justify-between">
                <div className="text-sm font-semibold text-gray-900">Contact</div>
                <div className="text-xs text-gray-500">Email, phone, fax</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Labeled label="Email">
                  <Input value={attorneyForm.email} onChange={(v) => updateAttorneyField('email', v)} placeholder="attorney@firm.com" type="email" />
                </Labeled>
                <Labeled label="Phone">
                  <Input value={attorneyForm.phone_number} onChange={(v) => updateAttorneyField('phone_number', v)} placeholder="(555) 123-4567" />
                </Labeled>
                <Labeled label="Fax">
                  <Input value={attorneyForm.fax_number} onChange={(v) => updateAttorneyField('fax_number', v)} placeholder="Optional" />
                </Labeled>
              </div>
            </div>

            {/* Office / Court */}
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="mb-3 flex items-center justify-between">
                <div className="text-sm font-semibold text-gray-900">Office & Court</div>
                <div className="text-xs text-gray-500">Address, district/division</div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Labeled label="Street">
                  <Input value={attorneyForm.street} onChange={(v) => updateAttorneyField('street', v)} placeholder="3119 Bristol Hwy" />
                </Labeled>
                <Labeled label="Street 2">
                  <Input value={attorneyForm.street2} onChange={(v) => updateAttorneyField('street2', v)} placeholder="Ste 217" />
                </Labeled>

                <div className="sm:col-span-2">
                  <div className="text-xs font-semibold text-gray-700">City / State / Zip</div>
                  <div className="mt-1 grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <Input value={attorneyForm.city} onChange={(v) => updateAttorneyField('city', v)} placeholder="City" />
                    <Input value={attorneyForm.state} onChange={(v) => updateAttorneyField('state', v)} placeholder="State" />
                    <Input value={attorneyForm.zip_code} onChange={(v) => updateAttorneyField('zip_code', v)} placeholder="Zip" />
                  </div>
                </div>

                <Labeled label="Filing District">
                  <Select
                    value={attorneyForm.filing_district}
                    onChange={(v) => updateAttorneyField('filing_district', v)}
                    options={districtOptions}
                  />
                </Labeled>
                <Labeled label="Division">
                  <Input value={attorneyForm.division} onChange={(v) => updateAttorneyField('division', v)} placeholder="Northeastern" />
                </Labeled>
              </div>
            </div>

            {/* Fees */}
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="mb-3 flex items-center justify-between">
                <div className="text-sm font-semibold text-gray-900">Fees</div>
                <div className="text-xs text-gray-500">Chapter 7 / Chapter 13 defaults</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Labeled label="Ch 7 Base Fee">
                  <Input type="number" value={attorneyForm.base_chapter_7} onChange={(v) => updateAttorneyField('base_chapter_7', v)} placeholder="1200" />
                </Labeled>
                <Labeled label="Ch 7 Joint Additional">
                  <Input type="number" value={attorneyForm.chapter_7_joint_additional} onChange={(v) => updateAttorneyField('chapter_7_joint_additional', v)} placeholder="100" />
                </Labeled>
                <Labeled label="Ch 7 Reaffirmation Additional">
                  <Input type="number" value={attorneyForm.chapter_7_reaffirmation_additional} onChange={(v) => updateAttorneyField('chapter_7_reaffirmation_additional', v)} placeholder="50" />
                </Labeled>
                <Labeled label="Ch 7 Self-Employed Additional">
                  <Input type="number" value={attorneyForm.chapter_7_self_employed_additional} onChange={(v) => updateAttorneyField('chapter_7_self_employed_additional', v)} placeholder="100" />
                </Labeled>
                <Labeled label="Ch 13 Unsecured Fee">
                  <Input type="number" value={attorneyForm.chapter_13_unsecured_fee} onChange={(v) => updateAttorneyField('chapter_13_unsecured_fee', v)} placeholder="4000" />
                </Labeled>
                <Labeled label="Ch 13 Max Fee">
                  <Input type="number" value={attorneyForm.chapter_13_max_fee} onChange={(v) => updateAttorneyField('chapter_13_max_fee', v)} placeholder="4500" />
                </Labeled>
              </div>
            </div>
          </div>

          <div className="mt-5 flex items-center justify-end gap-3">
            <Button variant="secondary" onClick={closeAttorneyModal} disabled={attorneyWorking}>
              Cancel
            </Button>
            <Button onClick={saveAttorney} disabled={attorneyWorking}>
              {attorneyWorking ? 'Saving…' : isEditingAttorney ? 'Save Changes' : 'Create Attorney'}
            </Button>
          </div>

          <div className="mt-4 text-xs text-gray-500">
            If you get a 401 here, make sure your firm JWT is stored in localStorage (accessToken).
          </div>
        </Modal>
      </div>
    </DashboardShell>
  );
}

export default FirmManagementPage;
export { FirmManagementPage };
